import {
  type IConnectToFreshStartDesktopRequest,
  type IConnectToMyDesktopRequest,
  type IConnectToParticipantDesktopRequest,
  type IConnectToTrainerDesktopRequest,
  type ICreateFreshStartFromOsAsyncTemplateRequest,
  type ICreateFreshStartFromStandaloneAsyncTemplateRequest,
  type IDeleteDesktopAsyncRequest,
  type IDeleteFreshStartDesktopRequest,
  type IGetCourseDesktopsRequest,
  type IGetCourseDesktopsResponse,
  type IGetMyDesktopsForCoursesRequest,
  type IGetMyDesktopsForCoursesResponse,
  type IGetParticipantsRequest,
  type IGetParticipantsResponse,
  type IGetStandalonesResponse,
  type IGetTrainerDesktopRequest,
  type IGetTrainerDesktopsForCoursesRequest,
  type IGetTrainerDesktopsForCoursesResponse,
  type IStartParticipantDesktopRequest,
  type IStopDesktopRequest
} from '@libs/models';

import { BaseApi, type Result } from './api-client';
import type { DesktopModel } from './index';

export class DesktopsApi extends BaseApi {
  protected readonly baseUrl = '/api/courses';

  async connectToMyDesktop(
    request: IConnectToMyDesktopRequest
  ): Promise<Result<DesktopModel | null>> {
    return this.post<DesktopModel | null>(
      '/connectToMyDesktop',
      request,
      'Connect to my desktop failed'
    );
  }

  async connectToTrainerDesktop(
    request: IConnectToTrainerDesktopRequest
  ): Promise<Result<DesktopModel | null>> {
    return this.post<DesktopModel | null>(
      '/connectToTrainerDesktop',
      request,
      'Connect to trainer desktop failed'
    );
  }

  async connectToFreshStartDesktop(
    request: IConnectToFreshStartDesktopRequest
  ): Promise<Result<DesktopModel | null>> {
    return this.post<DesktopModel | null>(
      '/connectToFreshStartDesktop',
      request,
      'Connect to fresh start desktop failed'
    );
  }

  async connectToParticipantDesktop(
    request: IConnectToParticipantDesktopRequest
  ): Promise<Result<DesktopModel | null>> {
    return this.post<DesktopModel | null>(
      '/connectToParticipantDesktop',
      request,
      'Connect to participant desktop failed'
    );
  }

  async getMyDesktop(courseId: string, signal?: AbortSignal): Promise<Result<DesktopModel | null>> {
    return this.get<DesktopModel | null>(
      '/myDesktop',
      { courseId },
      'Get my desktop failed',
      signal
    );
  }

  async getTrainerDesktop(
    request: IGetTrainerDesktopRequest,
    signal?: AbortSignal
  ): Promise<Result<DesktopModel | null>> {
    return this.get<DesktopModel | null>(
      '/trainerDesktop',
      request,
      'Get trainer desktop failed',
      signal
    );
  }

  async getFreshStartDesktop(
    courseId: string,
    signal?: AbortSignal
  ): Promise<Result<DesktopModel | null>> {
    return this.get<DesktopModel | null>(
      '/freshStartDesktop',
      { courseId },
      'Get fresh start desktop failed',
      signal
    );
  }

  async getCourseDesktops(
    request: IGetCourseDesktopsRequest,
    signal?: AbortSignal
  ): Promise<Result<IGetCourseDesktopsResponse>> {
    return this.get<IGetCourseDesktopsResponse>(
      '/desktops',
      request,
      'Get course desktops failed',
      signal
    );
  }

  async getMyDesktopsForCourses(
    request: IGetMyDesktopsForCoursesRequest
  ): Promise<Result<IGetMyDesktopsForCoursesResponse>> {
    return this.post<IGetMyDesktopsForCoursesResponse>(
      '/myDesktopsForCourses',
      request,
      'Get my desktops for courses failed'
    );
  }

  async getTrainerDesktopsForCourses(
    request: IGetTrainerDesktopsForCoursesRequest
  ): Promise<Result<IGetTrainerDesktopsForCoursesResponse>> {
    return this.post<IGetTrainerDesktopsForCoursesResponse>(
      '/trainerDesktopsForCourses',
      request,
      'Get trainer desktops for courses failed'
    );
  }

  async startParticipantDesktop(
    request: IStartParticipantDesktopRequest
  ): Promise<Result<DesktopModel | null>> {
    return this.post<DesktopModel | null>(
      '/startParticipantDesktop',
      request,
      'Start participant desktop failed'
    );
  }

  async getParticipants(
    request: IGetParticipantsRequest
  ): Promise<Result<IGetParticipantsResponse>> {
    return this.get<IGetParticipantsResponse>('/participants', request, 'Get participants failed');
  }

  async getStandalones(): Promise<Result<IGetStandalonesResponse>> {
    return this.get<IGetStandalonesResponse>('/getStandalones', null, 'Get standalones failed');
  }

  async stopDesktop(request: IStopDesktopRequest): Promise<void> {
    return this.postVoid('/stopDesktop', request);
  }

  async deleteDesktop(request: IDeleteDesktopAsyncRequest): Promise<void> {
    return this.postVoid('/deleteDesktop', request);
  }

  async deleteFreshStartDesktop(request: IDeleteFreshStartDesktopRequest): Promise<void> {
    return this.postVoid('/deleteFreshStartDesktop', request);
  }

  async createFreshStartFromStandaloneTemplate(
    request: ICreateFreshStartFromStandaloneAsyncTemplateRequest
  ): Promise<void> {
    return this.postVoid('/createFreshStartFromStandaloneTemplate', request);
  }

  async createFreshStartFromOsTemplate(
    request: ICreateFreshStartFromOsAsyncTemplateRequest
  ): Promise<void> {
    return this.postVoid('/createFreshStartFromOsTemplate', request);
  }
}
